import { useState, useEffect } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { HandleTableData } from '@wajeez-web/components/data-table';
import { AdminSystemPermissions } from '@wajeez-web/core/types';
import {
  useProductSelector,
  useProductDispatch,
  FAQsActions,
} from '@wajeez-web/stores/product';
import { Button } from '@wajeez-web/components/ui';
import { PlusIcon } from '@heroicons/react/solid';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { CheckBoxType } from '@wajeez-web/components/mui';

import FaqsModal from '../components/FaqsModal';
import FAQsCategoriesModal from '../components/FAQsCategoriesModal';
import { DataTableWrapper } from '@wajeez-web/components/helpers';
import { convertMarkdownToHtml } from '@wajeez-web/core/utils';

const FaqsPage = () => {
  const dispatch = useProductDispatch();

  const [faqModalState, setFaqModalState] = useState<{
    open: boolean;
    selected: number | null;
  }>({ open: false, selected: null });
  const [FAQCategoriesModalState, setFAQCategoriesModalState] = useState({
    open: false,
  });

  const { data, status } = useProductSelector((s) => s.faqs);

  const {
    getData,
    tablePreferances,
    setTablePreferences,
    isTableReferencesLoading,
  } = HandleTableData('faqs', FAQsActions.getFaqsDataTableAsync);

  const getFAQCatigories = () => {
    dispatch(FAQsActions.getFaqCtegoriesAsync());
  };

  useEffect(() => {
    getFAQCatigories();
  }, []);

  return (
    <div>
      <PageLayoutsContainer
        title="FAQs"
        endSideComponent={[
          {
            element: (
              <Button
                title={'Categories'}
                onClick={() => {
                  setFAQCategoriesModalState({ open: true });
                }}
                bg="primary"
                color="white"
              />
            ),
            permission: ['75.2', '75,3', '75', '4', '75.1'],
          },
          {
            element: (
              <Button
                title={'Add New FAQ'}
                bg={'primary'}
                color={'white'}
                Icon={PlusIcon}
                onClick={() =>
                  setFaqModalState({ ...faqModalState, open: true })
                }
              />
            ),
            permission: [AdminSystemPermissions.faqs.add],
          },
        ]}
        withoutRightPadding={true}
        breadCrumbsLinks={[{ title: 'FAQs' }]}
      >
        {
          <div className="  ">
            {
              <DataTableWrapper
                data={data as contentEntityDataType}
                onHeaderManage={(header) => {
                  const flags = header
                    ?.filter((h) => h.selected)
                    .reduce(
                      (previousValue: number, currentValue: CheckBoxType) =>
                        previousValue + Number(currentValue.value),
                      0
                    );

                  tablePreferances &&
                    flags &&
                    setTablePreferences({
                      ...tablePreferances,
                      flags: flags + 1,
                    });
                }}
                onPaginationChange={(pagination) => {
                  tablePreferances &&
                    setTablePreferences({
                      ...tablePreferances,
                      pageNumber: pagination.pageNumber,
                      pageSize: pagination.pageSize,
                    });
                }}
                sort={tablePreferances?.sortingOperations?.[0]}
                onSort={(sort) => {
                  tablePreferances &&
                    setTablePreferences({
                      ...tablePreferances,
                      sortingOperations: [sort],
                    });
                }}
                onFilter={(filters, pagination) => {
                  tablePreferances &&
                    setTablePreferences({
                      ...tablePreferances,
                      pageNumber: pagination.pageNumber,
                      filteringOperations: filters.map((f) => {
                        return {
                          property: Number(f.key),
                          cretiria: f.value,
                          comparisonType: f.operator,
                        };
                      }),
                    });
                }}
                isLoading={status === 'loading'}
                rowActions={(row) => {
                  return [
                    {
                      title: 'Edit',
                      permission: AdminSystemPermissions.faqs.edit,
                      type: 'request',
                      endpoint: '',
                      action: () => {
                        setFaqModalState({
                          open: true,
                          selected: Number(row[1]),
                        });
                      },
                    },
                    {
                      title: 'Delete',
                      permission: AdminSystemPermissions.faqs.delete,
                      type: 'request',
                      endpoint: '',
                      action: async () => {
                        const res = await dispatch(
                          FAQsActions.deleteFaqAsync(Number(row[1]))
                        );
                        if (res.meta.requestStatus === 'fulfilled') {
                          getData();
                        }
                      },
                    },
                  ];
                }}
                preferences={tablePreferances || undefined}
                setPreferences={setTablePreferences}
                columnsSelectors={(r) => {
                  return {
                    '4': (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: convertMarkdownToHtml(r[4] as string)! || '',
                        }}
                      />
                    ),
                  };
                }}
              />
            }
          </div>
        }
      </PageLayoutsContainer>
      {faqModalState.open && (
        <FaqsModal
          id={faqModalState.selected || undefined}
          isOpen={faqModalState.open}
          onClose={() => setFaqModalState({ open: false, selected: null })}
          onCreateNewFaqCategory={async (val) => {
            const res = await dispatch(
              FAQsActions.createNewCategoryAsync({ name: val, languageId: 1 })
            );
            if (res.meta.requestStatus === 'fulfilled') {
              getFAQCatigories();
            }
          }}
          onSubmit={async (data) => {
            const res = await dispatch(
              faqModalState.selected
                ? FAQsActions.editFaqAsync({
                  id: faqModalState.selected,
                  ...data,
                  languageId: 1,
                })
                : FAQsActions.createNewFaqAsync({ ...data, languageId: 1 })
            );
            if (res.meta.requestStatus === 'fulfilled') {
              setFaqModalState({ open: false, selected: null });
              getData();
            }
          }}
        />
      )}
      {FAQCategoriesModalState.open && (
        <FAQsCategoriesModal
          isOpen={FAQCategoriesModalState.open}
          getData={getFAQCatigories}
          onClose={() => setFAQCategoriesModalState({ open: false })}
        />
      )}
    </div>
  );
};

export default FaqsPage;
